import { useMemo, useState } from 'react'
import {
  Accordion, ActionIcon, Alert, Badge, Box, Group, Loader, Select, Stack, Text,
  TextInput, Title, Tooltip, UnstyledButton,
} from '@mantine/core'
import {
  IconAlertTriangle, IconBuilding, IconInfoCircle, IconSearch, IconTrash,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  channelLabel, notificationTemplatesApi,
  type NotificationTemplateListItem,
} from '@/api/notification-templates'
import { organizationsApi } from '@/api/organizations'
import { useManagedOrganizations } from '@/hooks/useOrganizations'
import { useCan } from '@/store/auth'
import { MensagemEditorDrawer } from './MensagemEditorDrawer'

/** Valor do seletor que representa o escopo de plataforma (não é um `org_id`). */
const ESCOPO_PLATAFORMA = 'plataforma'

/** Agrupa por domínio preservando a ordem em que os domínios aparecem no catálogo. */
function agruparPorDominio(items: NotificationTemplateListItem[]) {
  const grupos: { domain: string; label: string; items: NotificationTemplateListItem[] }[] = []
  for (const item of items) {
    let grupo = grupos.find(g => g.domain === item.domain)
    if (!grupo) {
      grupo = { domain: item.domain, label: item.domain_label || item.domain, items: [] }
      grupos.push(grupo)
    }
    grupo.items.push(item)
  }
  return grupos
}

function combina(item: NotificationTemplateListItem, busca: string): boolean {
  const termo = busca.trim().toLowerCase()
  if (!termo) return true
  return (
    item.label.toLowerCase().includes(termo) ||
    item.description.toLowerCase().includes(termo) ||
    item.event_key.toLowerCase().includes(termo)
  )
}

/** Uma linha do catálogo. Só os itens editáveis abrem o editor. */
function LinhaMensagem({ item, escopoOrg, onEditar }: {
  item: NotificationTemplateListItem
  /** No escopo de org o selo distingue *quem* personalizou (spec §2.4). */
  escopoOrg: boolean
  onEditar: (item: NotificationTemplateListItem) => void
}) {
  // No escopo de org, "personalizado" sem qualificação enganaria o gestor: o
  // texto pode diferir do padrão porque a *plataforma* mexeu, não ele.
  const selo = escopoOrg && item.source
    ? (item.source === 'org'
        ? { texto: 'personalizado por esta organização', cor: 'orange' }
        : item.source === 'plataforma'
          ? { texto: 'texto da plataforma', cor: 'blue' }
          : null)
    : (item.is_customized ? { texto: 'personalizado', cor: 'orange' } : null)

  const conteudo = (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Group gap="xs" wrap="wrap">
        <Text size="sm" fw={600}>{item.label}</Text>
        <Badge size="xs" variant="light">{channelLabel(item.channel)}</Badge>
        {selo && (
          <Badge size="xs" color={selo.cor} variant="light">{selo.texto}</Badge>
        )}
        {!item.editable && (
          <Badge size="xs" color="gray" variant="light">edição em breve</Badge>
        )}
      </Group>
      <Text size="xs" c="dimmed">{item.description}</Text>
      <Text size="xs" c="dimmed">Quem recebe: {item.recipients_note}</Text>
    </Stack>
  )

  // "Edição em breve": informativo, sem editor. O texto desses eventos continua
  // sendo montado no código, então mostrar um editor seria mentira na tela.
  if (!item.editable) {
    return (
      <Box
        p="xs"
        style={{ borderTop: '1px solid var(--ai-border-light)', opacity: 0.75 }}
      >
        {conteudo}
      </Box>
    )
  }

  return (
    <UnstyledButton
      aria-label={`Editar mensagem: ${item.label} (${channelLabel(item.channel)})`}
      onClick={() => onEditar(item)}
      p="xs"
      style={{ borderTop: '1px solid var(--ai-border-light)', display: 'block', width: '100%' }}
    >
      {conteudo}
    </UnstyledButton>
  )
}

/**
 * Aviso de overrides órfãos (spec 2026-07-27 §4) — só no escopo de plataforma.
 *
 * São textos de eventos que saíram do registry: a lista não os mostra (ela
 * deriva do catálogo) e ninguém os apaga. Sem este aviso ficam invisíveis e
 * para sempre.
 */
function AvisoOrfaos() {
  const qc = useQueryClient()

  const { data: orfaos = [] } = useQuery({
    queryKey: ['notification-template-orphans'],
    queryFn: () => notificationTemplatesApi.orphans(),
    // Endpoint novo: um 404 numa instância mais antiga não deve encher a tela
    // de erro — o aviso simplesmente não aparece.
    retry: false,
  })

  const apagar = useMutation({
    mutationFn: ({ eventKey, channel }: { eventKey: string; channel: string }) =>
      notificationTemplatesApi.deleteOrphan(eventKey, channel),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notification-template-orphans'] })
      notifications.show({
        title: 'Texto órfão apagado',
        message: 'O evento não existe mais no catálogo — nada deixa de ser enviado.',
        color: 'green',
      })
    },
    onError: () => {
      notifications.show({
        title: 'Falha ao apagar',
        message: 'Não foi possível apagar o texto órfão. Tente novamente.',
        color: 'red',
      })
    },
  })

  if (orfaos.length === 0) return null

  return (
    <Alert
      color="yellow"
      variant="light"
      icon={<IconAlertTriangle size={18} />}
      title="Textos personalizados sem evento no catálogo"
    >
      <Stack gap="xs">
        <Text size="sm">
          Estes textos foram personalizados para eventos que não existem mais.
          Não são enviados a ninguém — apagar apenas limpa o registro.
        </Text>
        {orfaos.map(o => (
          <Group key={`${o.event_key}:${o.channel}`} gap="xs" wrap="nowrap">
            <Text size="sm" ff="monospace" style={{ minWidth: 0 }}>
              {o.event_key}/{channelLabel(o.channel)}
            </Text>
            <Tooltip label="Apagar o texto órfão" withArrow>
              <ActionIcon
                aria-label={`Apagar texto órfão: ${o.event_key} (${channelLabel(o.channel)})`}
                color="red"
                variant="subtle"
                size="sm"
                loading={apagar.isPending}
                onClick={() => apagar.mutate({ eventKey: o.event_key, channel: o.channel })}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ))}
      </Stack>
    </Alert>
  )
}

/**
 * Tela "Mensagens do sistema" (spec 2026-07-25 §9) — `/admin/notificacoes`.
 *
 * O nome importa: `/notificacoes/preferencias` é o **usuário escolhendo o que
 * recebe**; esta é o **Admin definindo o que o sistema diz**.
 *
 * Dois escopos (spec 2026-07-27 §2.5): plataforma e Organização. O gestor que
 * gere uma única org entra direto nela, sem seletor — para ele não existe
 * "escopo" a escolher.
 */
export function MensagensSistema() {
  const can = useCan()
  const isAdmin = can('admin', 'platform')
  const podePlataforma = can('notificacoes', 'template_write') || isAdmin

  // Orgs do seletor. Para o Admin, `_can_manage_org` é verdadeiro em todas —
  // então a fonte é a lista completa; para o gestor, só as que ele gere
  // (`useManagedOrganizations` não roda para admin, por desenho).
  const { data: orgsGeridas = [], isLoading: carregandoGeridas } = useManagedOrganizations()
  const { data: todasOrgs = [], isLoading: carregandoTodas } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => organizationsApi.list(),
    enabled: isAdmin,
  })
  const orgs = isAdmin ? todasOrgs : orgsGeridas
  const carregandoOrgs = isAdmin ? carregandoTodas : carregandoGeridas

  const [escopoEscolhido, setEscopoEscolhido] = useState<string | null>(null)
  const [busca, setBusca] = useState('')
  const [selecionado, setSelecionado] = useState<NotificationTemplateListItem | null>(null)
  const [abertos, setAbertos] = useState<string[] | null>(null)

  const opcoesEscopo = useMemo(() => {
    const opcoes = podePlataforma
      ? [{ value: ESCOPO_PLATAFORMA, label: 'Plataforma (todas as organizações)' }]
      : []
    for (const org of orgs) {
      opcoes.push({ value: String(org.id), label: org.name })
    }
    return opcoes
  }, [podePlataforma, orgs])

  // Quem não pode editar a plataforma cai na primeira org que gere — daí "o
  // gestor de uma única org entra já nela".
  const escopoPadrao = podePlataforma
    ? ESCOPO_PLATAFORMA
    : (orgs.length > 0 ? String(orgs[0].id) : null)
  const escopo = escopoEscolhido ?? escopoPadrao
  const orgId = escopo && escopo !== ESCOPO_PLATAFORMA ? Number(escopo) : null
  const orgAtual = orgs.find(o => o.id === orgId) ?? null

  const podeVer = podePlataforma || orgs.length > 0

  const { data: escopoData, isLoading, isError, error } = useQuery({
    queryKey: ['notification-templates', orgId],
    queryFn: () => notificationTemplatesApi.listWithScope(orgId),
    enabled: podeVer,
  })
  const items = escopoData?.items
  // Alçada (spec 2026-07-29 §7): quando a capacidade `mensagens` foi retirada deste
  // gestor nesta Organização, a rota devolve o motivo. Exibir é melhor que deixá-lo
  // descobrir por um 403 ao salvar.
  const restricao = escopoData?.restricao ?? null

  const grupos = useMemo(() => {
    const filtrados = (items ?? [])
      // O backend já devolve só os `org_overridable` no escopo de org; repetir
      // aqui evita que um item não editável por org apareça por engano.
      .filter(i => (orgId == null ? true : i.org_overridable !== false))
      .filter(i => combina(i, busca))
    return agruparPorDominio(filtrados)
  }, [items, busca, orgId])

  // Sem busca: tudo aberto (o catálogo é curto). Com busca: os grupos que
  // sobraram, para o resultado não ficar escondido atrás de um clique.
  const valorAccordion = busca.trim() || abertos === null
    ? grupos.map(g => g.domain)
    : abertos

  // Enquanto as orgs geridas não chegam não se sabe se há acesso — recusar
  // agora mostraria "acesso restrito" a quem tem permissão.
  if (!podePlataforma && carregandoOrgs) {
    return (
      <Stack gap="md" p="xl">
        <Loader size="sm" />
      </Stack>
    )
  }

  if (!podeVer) {
    return (
      <Stack gap="md" p="xl">
        <Alert color="yellow" title="Acesso restrito" icon={<IconAlertTriangle size={18} />}>
          Você não possui permissão para editar as mensagens do sistema.
        </Alert>
      </Stack>
    )
  }

  return (
    <Stack gap="md" p="xl">
      <Box>
        <Title order={3}>Mensagens do sistema</Title>
        <Text size="sm" c="dimmed">
          Textos que o ConsoleAI envia por e-mail e publica no feed. Aqui você define
          o que o sistema diz — para escolher o que <b>você</b> recebe, use
          Notificações &gt; Preferências.
        </Text>
      </Box>

      {/* Uma opção só: não há escopo a escolher (Admin sem orgs, ou gestor de
          uma única org). O seletor apareceria como um campo travado. */}
      {opcoesEscopo.length > 1 && (
        <Select
          label="Escopo dos textos"
          description="Os textos da Organização valem só para quem é membro dela."
          leftSection={<IconBuilding size={16} />}
          data={opcoesEscopo}
          value={escopo}
          onChange={(v) => {
            setEscopoEscolhido(v ?? escopoPadrao)
            setSelecionado(null)
          }}
          allowDeselect={false}
          maw={420}
        />
      )}

      {orgId != null && restricao == null && (
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          Você está editando os textos de <b>{orgAtual?.name ?? 'sua organização'}</b>.
          Eles substituem os textos da plataforma apenas para quem é membro desta
          Organização — quem não é continua recebendo o texto da plataforma.
        </Alert>
      )}

      {/* Alçada retirada (spec 2026-07-29 §7): o motivo registrado no lugar de um
          botão que só sabe responder 403. Substitui o aviso azul acima porque a
          informação de que ele NÃO pode editar é a mais importante da tela. */}
      {orgId != null && restricao != null && (
        <Alert color="orange" variant="light" title="Edição indisponível nesta Organização"
               icon={<IconAlertTriangle size={18} />}>
          <Stack gap={4}>
            <Text size="sm">
              Um administrador retirou de você a edição das mensagens de{' '}
              <b>{orgAtual?.name ?? 'sua organização'}</b>. Você continua vendo os
              textos que a Organização envia, mas não pode alterá-los.
            </Text>
            <Text size="sm"><b>Motivo:</b> {restricao.motivo}</Text>
          </Stack>
        </Alert>
      )}

      {/* Órfão é sujeira do escopo de plataforma: a lista por org já é filtrada
          pelo registry e não tem como carregar evento extinto. */}
      {orgId == null && podePlataforma && <AvisoOrfaos />}

      <TextInput
        placeholder="Buscar por rótulo ou descrição"
        leftSection={<IconSearch size={16} />}
        value={busca}
        onChange={(e) => setBusca(e.currentTarget.value)}
        maw={420}
      />

      {isLoading && <Loader size="sm" />}

      {isError && (
        <Alert color="red" title="Falha ao carregar o catálogo" icon={<IconAlertTriangle size={18} />}>
          {(error as Error)?.message ?? 'Tente novamente em instantes.'}
        </Alert>
      )}

      {!isLoading && !isError && grupos.length === 0 && (
        <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
          Nenhuma mensagem corresponde à busca.
        </Alert>
      )}

      {grupos.length > 0 && (
        <Accordion multiple value={valorAccordion} onChange={setAbertos} variant="separated">
          {grupos.map(grupo => (
            <Accordion.Item key={grupo.domain} value={grupo.domain}>
              <Accordion.Control>
                <Group gap="xs">
                  <Text fw={600} size="sm">{grupo.label}</Text>
                  <Badge size="xs" variant="light" color="gray">{grupo.items.length}</Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap={0}>
                  {grupo.items.map(item => (
                    <LinhaMensagem
                      key={`${item.event_key}:${item.channel}`}
                      item={item}
                      escopoOrg={orgId != null}
                      onEditar={setSelecionado}
                    />
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      <MensagemEditorDrawer
        eventKey={selecionado?.event_key ?? null}
        channel={selecionado?.channel ?? null}
        orgId={orgId}
        orgName={orgAtual?.name ?? null}
        opened={selecionado !== null}
        onClose={() => setSelecionado(null)}
        updatedByEmail={selecionado?.updated_by_email ?? null}
        updatedAt={selecionado?.updated_at ?? null}
      />
    </Stack>
  )
}
